import { reportsPosition } from '../GscTools/metrics';
import { REPORTING_TIMEZONE, formatPeriod, searchTypeLabel } from '../GscTools/period';
import { segmentKeyLabel, segmentLabel } from './segments';

/**
 * The CSV of one segmentation: the rows of the active dimension, and the scope they were read
 * under.
 *
 * One partition per file, deliberately. Country rows and appearance rows cut the same traffic two
 * different ways and overlap each other, so a file holding both would be a table whose column of
 * clicks sums to more than the property got. The active dimension is named in the metadata and is
 * the only dimension in the body.
 *
 * Both the returned key and its readable label are columns, so a row can be matched back to the
 * request that produced it without having to guess which of the two Search Console was given.
 * A metric the API did not return stays empty rather than becoming a 0.
 */
export function buildSegmentCsv({ t, scope, rows, fetchedAt, returnedRows }) {
    const hasPosition = reportsPosition(scope.searchType);

    const meta = [
        [t('tools.audience_appearance.export.report'), t('tools.audience_appearance.header')],
        [t('tools.gsc.exports.property'), scope.siteUrl],
        [t('tools.audience_appearance.export.dimension'), scope.segment],
        [t('tools.gsc.coverage.current'), formatPeriod(scope.current)],
        [t('tools.gsc.coverage.baseline'), scope.baseline ? formatPeriod(scope.baseline) : ''],
        [t('tools.gsc.controls.search_type'), searchTypeLabel(scope.searchType, t)],
        [
            t('tools.gsc.controls.country'),
            scope.takesFilters
                ? scope.country || 'all'
                : t('tools.audience_appearance.export.not_sent'),
        ],
        [
            t('tools.gsc.controls.device'),
            scope.takesFilters
                ? scope.device || 'all'
                : t('tools.audience_appearance.export.not_sent'),
        ],
        [
            t('tools.audience_appearance.export.page_rule'),
            scope.takesFilters && scope.pageRegex
                ? scope.pageRegex
                : t('tools.audience_appearance.coverage.not_applied'),
        ],
        [t('tools.gsc.exports.data_state'), t(`tools.gsc.data_state.${scope.dataState}`)],
        [t('tools.gsc.exports.timezone'), REPORTING_TIMEZONE],
        [t('tools.audience_appearance.export.returned'), returnedRows],
        [
            t('tools.audience_appearance.export.coverage'),
            t('tools.audience_appearance.coverage.coverage_value'),
        ],
        [t('tools.gsc.exports.fetched_at'), fetchedAt ?? ''],
        [],
    ];

    const header = [
        segmentLabel(t, scope.segment),
        t('tools.audience_appearance.export.segment_label'),
        t('tools.gsc.columns.current_clicks'),
        t('tools.gsc.columns.prior_clicks'),
        t('tools.gsc.columns.delta_clicks'),
        t('tools.gsc.columns.delta_percent'),
        t('tools.gsc.columns.current_impressions'),
        t('tools.gsc.columns.prior_impressions'),
        t('tools.gsc.columns.current_ctr'),
        t('tools.gsc.columns.prior_ctr'),
        ...(hasPosition
            ? [t('tools.gsc.columns.current_position'), t('tools.gsc.columns.prior_position')]
            : []),
    ];

    const body = rows.map((row) => [
        row.key,
        segmentKeyLabel(t, scope.segment, row.key),
        row.current?.clicks,
        row.prior?.clicks,
        row.deltaClicks,
        row.deltaPercent,
        row.current?.impressions,
        row.prior?.impressions,
        row.current?.ctr,
        row.prior?.ctr,
        ...(hasPosition ? [row.current?.position, row.prior?.position] : []),
    ]);

    return [...meta, header, ...body];
}
